/**
 * @fileoverview Feedback Hook
 * 
 * Hook for submitting user feedback on the app experience.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { logEventDirect } from '@/hooks/useEventLogger';

interface Feedback {
  id: string;
  user_id: string | null;
  content: string;
  source: 'text' | 'voice';
  status: 'new' | 'reviewed' | 'planned' | 'done' | 'dismissed';
  admin_notes: string | null;
  created_at: string;
  reviewed_at: string | null;
  reviewed_by: string | null;
}

interface SubmitFeedbackParams {
  content: string;
  source: 'text' | 'voice';
  attachmentUrl?: string | null;
  attachmentType?: string | null;
}

export function useFeedback() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const submitFeedback = useMutation({
    mutationFn: async ({ content, source, attachmentUrl, attachmentType }: SubmitFeedbackParams) => {
      if (!user) throw new Error('Must be logged in to submit feedback');
      
      const { data, error } = await supabase
        .from('feedback')
        .insert({
          user_id: user.id,
          content,
          source,
          attachment_url: attachmentUrl || null,
          attachment_type: attachmentType || null,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['user-feedback'] });
      
      // Log event for analytics
      logEventDirect('feedback_submitted', 'engagement', {
        source: data.source,
        has_attachment: !!data.attachment_url,
      }, user?.id);
    },
  });

  // For users to see their own feedback
  const { data: userFeedback, isLoading: isLoadingUserFeedback } = useQuery({
    queryKey: ['user-feedback', user?.id],
    queryFn: async () => {
      if (!user) return [];
      
      const { data, error } = await supabase
        .from('feedback')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data as Feedback[];
    },
    enabled: !!user,
  });

  return {
    submitFeedback,
    userFeedback,
    isLoadingUserFeedback,
  };
}

// Admin hook for managing all feedback
export function useAdminFeedback() {
  const queryClient = useQueryClient();

  const { data: allFeedback, isLoading } = useQuery({
    queryKey: ['admin-feedback'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('feedback')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data as Feedback[];
    },
  });

  const updateFeedback = useMutation({
    mutationFn: async ({ 
      id, 
      status, 
      admin_notes 
    }: { 
      id: string; 
      status?: Feedback['status']; 
      admin_notes?: string;
    }) => {
      const updates: Partial<Feedback> = {};
      if (status) {
        updates.status = status;
        if (status !== 'new') {
          updates.reviewed_at = new Date().toISOString();
        }
      }
      if (admin_notes !== undefined) {
        updates.admin_notes = admin_notes;
      }

      const { data, error } = await supabase
        .from('feedback')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-feedback'] });
    },
  });

  // Get counts by status
  const statusCounts = allFeedback?.reduce((acc, f) => {
    acc[f.status] = (acc[f.status] || 0) + 1;
    return acc;
  }, {} as Record<string, number>) || {};

  return {
    allFeedback,
    isLoading,
    updateFeedback,
    statusCounts,
    newCount: statusCounts.new || 0,
  };
}
